/**
 * Whitelisted expression evaluator for canonical measurement formulas (AP-731).
 *
 * Grammar (stored already-normalized — no runtime normalization):
 *
 *   expr     = additive
 *   additive = multiplicative (('+' | '-') multiplicative)*        // left assoc
 *   mult     = unary (('*' | '/') unary)*                          // left assoc
 *   unary    = '-' unary | power
 *   power    = primary ('^' unary)?                                // right assoc
 *   primary  = NUMBER | 'PI' | 'sqrt' '(' expr ')' | '(' expr ')' | IDENT
 *
 * Precedence: '^' (right) > unary '-' > '* /' (left) > '+ -' (left).
 *
 * Identifiers resolve by EXACT, case-sensitive key match via the caller's
 * `resolve`. An identifier the resolver can't supply is a *missing operand*
 * (R9) — collected, not an error. Malformed syntax (an out-of-grammar
 * character such as a Unicode operator, an unbalanced paren) throws: the
 * canonical library is audited conforming, so this is exceptional and
 * never silently "fixed up". No `eval`, no `Function`.
 */

export type EvalResult = { value: number } | { missing: string[] };

type Token =
  | { kind: 'num'; value: number }
  | { kind: 'ident'; value: string }
  | { kind: 'op'; value: '+' | '-' | '*' | '/' | '^' | '(' | ')' };

const OPERATORS = new Set(['+', '-', '*', '/', '^', '(', ')']);

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i += 1;
    } else if (OPERATORS.has(ch)) {
      tokens.push({
        kind: 'op',
        value: ch as (Token & { kind: 'op' })['value'],
      });
      i += 1;
    } else if ((ch >= '0' && ch <= '9') || ch === '.') {
      let j = i + 1;
      while (j < input.length && /[0-9.]/.test(input[j])) j += 1;
      const num = Number(input.slice(i, j));
      if (Number.isNaN(num)) {
        throw new Error(`Invalid numeric literal at position ${i}`);
      }
      tokens.push({ kind: 'num', value: num });
      i = j;
    } else if (/[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < input.length && /[A-Za-z0-9_]/.test(input[j])) j += 1;
      tokens.push({ kind: 'ident', value: input.slice(i, j) });
      i = j;
    } else {
      throw new Error(`Unexpected character "${ch}" at position ${i}`);
    }
  }
  return tokens;
}

/**
 * Evaluate a canonical formula. Returns `{ value }` when every identifier
 * resolved, or `{ missing }` listing the unresolved identifier keys (in first-
 * seen order, de-duplicated). Throws on malformed (non-conforming) input.
 */
export function evaluate(
  formula: string,
  resolve: (key: string) => number | undefined
): EvalResult {
  const tokens = tokenize(formula);
  let pos = 0;
  const missing: string[] = [];

  const peek = (): Token | undefined => tokens[pos];
  const eat = (): Token => {
    const t = tokens[pos];
    if (!t) throw new Error('Unexpected end of formula');
    pos += 1;
    return t;
  };
  const expectOp = (op: string): void => {
    const t = eat();
    if (t.kind !== 'op' || t.value !== op) {
      throw new Error(`Expected "${op}"`);
    }
  };
  const isOp = (op: string): boolean => {
    const t = peek();
    return !!t && t.kind === 'op' && t.value === op;
  };

  const parseExpr = (): number => parseAdditive();

  function parseAdditive(): number {
    let left = parseMultiplicative();
    while (isOp('+') || isOp('-')) {
      const op = (eat() as { value: string }).value;
      const right = parseMultiplicative();
      left = op === '+' ? left + right : left - right;
    }
    return left;
  }

  function parseMultiplicative(): number {
    let left = parseUnary();
    while (isOp('*') || isOp('/')) {
      const op = (eat() as { value: string }).value;
      const right = parseUnary();
      left = op === '*' ? left * right : left / right;
    }
    return left;
  }

  function parseUnary(): number {
    if (isOp('-')) {
      eat();
      return -parseUnary();
    }
    return parsePower();
  }

  function parsePower(): number {
    const base = parsePrimary();
    if (isOp('^')) {
      eat();
      const exponent = parseUnary(); // right-associative
      return base ** exponent;
    }
    return base;
  }

  function parsePrimary(): number {
    const t = eat();
    if (t.kind === 'num') return t.value;
    if (t.kind === 'op' && t.value === '(') {
      const inner = parseExpr();
      expectOp(')');
      return inner;
    }
    if (t.kind === 'ident') {
      if (t.value === 'PI') return Math.PI;
      if (t.value === 'sqrt') {
        expectOp('(');
        const arg = parseExpr();
        expectOp(')');
        return Math.sqrt(arg);
      }
      const resolved = resolve(t.value);
      if (resolved === undefined) {
        if (!missing.includes(t.value)) missing.push(t.value);
        return Number.NaN; // placeholder; result discarded when `missing`
      }
      return resolved;
    }
    throw new Error(`Unexpected token "${JSON.stringify(t)}"`);
  }

  const value = parseExpr();
  if (pos !== tokens.length) throw new Error('Unexpected trailing tokens');
  if (missing.length > 0) return { missing };
  return { value };
}
